import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ArrowRight, Mail, Phone, Lock, User2, AlertCircle } from "lucide-react";
import Logo from "../components/Logo";
import TimeBackground from "../components/TimeBackground";
import { useAuth } from "../lib/auth";

function scorePassword(p) {
  if (!p) return 0;
  let s = 0;
  if (p.length >= 6) s += 1;
  if (p.length >= 10) s += 1;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s += 1;
  if (/\d/.test(p)) s += 1;
  if (/[^A-Za-z0-9]/.test(p)) s += 1;
  return Math.min(s, 4);
}
const strengthLabel = ["Too short", "Weak", "Fair", "Strong", "Excellent"];

export default function Auth() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, signup, login, loading } = useAuth();

  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "signup");
  const [channel, setChannel] = useState("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      nav(user.onboarded ? "/home" : "/onboarding", { replace: true });
    }
  }, [user, loading, nav]);

  const pwScore = useMemo(() => scorePassword(password), [password]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (channel === "phone") return;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!emailOk) return setErr("Please enter a valid email.");
    if (password.length < 6) return setErr("Password must be at least 6 characters.");
    if (mode === "signup" && name.trim().length < 1) return setErr("Please share your name.");

    try {
      setBusy(true);
      if (mode === "signup") {
        const u = await signup(name.trim(), email.trim().toLowerCase(), password);
        nav(u.onboarded ? "/home" : "/onboarding", { replace: true });
      } else {
        const u = await login(email.trim().toLowerCase(), password);
        nav(u.onboarded ? "/home" : "/onboarding", { replace: true });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went awry. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <TimeBackground overlay={0.72}>
      <div className="min-h-screen flex flex-col max-w-md mx-auto w-full px-6 py-10">
        <div className="flex-1 flex flex-col items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-8"
          >
            <div className="flex justify-center mb-5">
              <Logo size={64} />
            </div>
            <h1 className="font-ui font-semibold text-[26px] text-gold-gradient">
              {mode === "signup" ? "Begin your journey" : "Welcome back"}
            </h1>
            <p className="font-ui text-[#f8ecd4]/70 mt-2 text-[14px] leading-[1.5]">
              {mode === "signup"
                ? "The stories are already looking for you."
                : "The voice has been waiting."}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="glass rounded-[22px] p-6 w-full shadow-2xl"
          >
            {/* Email / Phone tabs */}
            <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-black/30 border border-[#d4af7a]/20 mb-5">
              <button
                data-testid="tab-email"
                onClick={() => setChannel("email")}
                className={`flex items-center justify-center gap-2 py-2 rounded-full text-sm font-ui transition-all ${
                  channel === "email"
                    ? "bg-gradient-to-r from-[#eac88a] to-[#a07544] text-[#1a0d1d]"
                    : "text-[#ac9470] hover:text-[#f8ecd4]"
                }`}
              >
                <Mail size={15} strokeWidth={1.75} /> Email
              </button>
              <button
                data-testid="tab-phone"
                onClick={() => setChannel("phone")}
                className={`flex items-center justify-center gap-2 py-2 rounded-full text-sm font-ui transition-all ${
                  channel === "phone"
                    ? "bg-gradient-to-r from-[#eac88a] to-[#a07544] text-[#1a0d1d]"
                    : "text-[#ac9470] hover:text-[#f8ecd4]"
                }`}
              >
                <Phone size={15} strokeWidth={1.75} /> Phone
              </button>
            </div>

            <AnimatePresence mode="wait">
              {channel === "phone" ? (
                <motion.div
                  key="phone"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="py-8 text-center space-y-3"
                >
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#d4af7a]/30 text-[10px] tracking-[0.3em] uppercase text-[#d4af7a]">
                    Coming soon
                  </div>
                  <p className="font-ui text-[#f8ecd4]/85 text-[15px] leading-[1.6]">
                    Phone sign-in will arrive with Narrava's next moon.
                  </p>
                  <p className="text-[#ac9470] text-sm font-ui">
                    For now, please continue with email.
                  </p>
                  <button
                    data-testid="switch-to-email"
                    onClick={() => setChannel("email")}
                    className="btn-outline-gold rounded-full px-5 py-2.5 mt-2 text-sm"
                  >
                    Use email instead
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="email"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={submit}
                  className="space-y-4"
                >
                  {mode === "signup" && (
                    <div className="space-y-1.5">
                      <label className="text-[11px] tracking-[0.2em] uppercase text-[#ac9470] font-ui">
                        Your name
                      </label>
                      <div className="relative">
                        <User2
                          size={16}
                          strokeWidth={1.75}
                          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ac9470]"
                        />
                        <input
                          data-testid="auth-name-input"
                          className="nv-input pl-10"
                          placeholder="What shall we call you?"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          autoComplete="name"
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="text-[11px] tracking-[0.2em] uppercase text-[#ac9470] font-ui">
                      Email
                    </label>
                    <div className="relative">
                      <Mail
                        size={16}
                        strokeWidth={1.75}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ac9470]"
                      />
                      <input
                        data-testid="auth-email-input"
                        type="email"
                        className="nv-input pl-10"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] tracking-[0.2em] uppercase text-[#ac9470] font-ui">
                      Password
                    </label>
                    <div className="relative">
                      <Lock
                        size={16}
                        strokeWidth={1.75}
                        className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#ac9470]"
                      />
                      <input
                        data-testid="auth-password-input"
                        type={show ? "text" : "password"}
                        className="nv-input pl-10 pr-12"
                        placeholder="At least 6 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete={mode === "signup" ? "new-password" : "current-password"}
                      />
                      <button
                        type="button"
                        data-testid="password-toggle"
                        onClick={() => setShow((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-[#ac9470] hover:text-[#f8ecd4] transition-colors"
                        aria-label="Toggle password visibility"
                      >
                        {show ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>

                    {mode === "signup" && password.length > 0 && (
                      <div className="flex items-center gap-1.5 pt-1">
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all ${
                              i < pwScore
                                ? "bg-gradient-to-r from-[#eac88a] to-[#a07544]"
                                : "bg-white/10"
                            }`}
                          />
                        ))}
                        <span
                          data-testid="password-strength"
                          className="text-[10px] tracking-wider uppercase text-[#ac9470] font-ui ml-2 whitespace-nowrap"
                        >
                          {strengthLabel[pwScore]}
                        </span>
                      </div>
                    )}
                  </div>

                  {err && (
                    <div
                      data-testid="auth-error"
                      className="flex items-start gap-2 text-sm text-[#ff9e88] bg-red-900/20 border border-red-500/20 rounded-xl px-3 py-2.5"
                    >
                      <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
                      <span className="font-ui">{err}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="auth-submit-btn"
                    disabled={busy}
                    className="btn-gold w-full rounded-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    <span className="font-ui">
                      {busy
                        ? "One moment…"
                        : mode === "signup"
                        ? "Create account"
                        : "Sign in"}
                    </span>
                    {!busy && <ArrowRight size={17} strokeWidth={2} />}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>

            <div className="text-center text-sm text-[#ac9470] font-ui pt-5">
              {mode === "signup" ? (
                <>
                  Already a seeker?{" "}
                  <button
                    data-testid="switch-to-login"
                    onClick={() => setMode("login")}
                    className="text-[#f0d09c] hover:text-[#f8ecd4] transition-colors"
                  >
                    Sign in
                  </button>
                </>
              ) : (
                <>
                  New here?{" "}
                  <button
                    data-testid="switch-to-signup"
                    onClick={() => setMode("signup")}
                    className="text-[#f0d09c] hover:text-[#f8ecd4] transition-colors"
                  >
                    Create an account
                  </button>
                </>
              )}
            </div>
          </motion.div>

          <Link
            to="/"
            className="text-center block pt-6 text-xs tracking-[0.18em] uppercase text-[#ac9470]/70 hover:text-[#f8ecd4] font-ui"
            data-testid="back-to-landing"
          >
            ← Return to the entrance
          </Link>
        </div>
      </div>
    </TimeBackground>
  );
}
